/**
 * Reseller (Sub-Master) Dashboard
 * Restricted data surface: dealer list + billing (retail/cost/margin) +
 * aggregate stats only (total leads count, total hot-return conversions).
 *
 * Resellers CANNOT:
 *   - View individual lead records
 *   - Drill into a dealer's full dashboard
 *   - See branding, offers, pricing rules, or inventory
 *
 * Clicking a dealer row opens a small read-only modal with the allowed
 * aggregates only — no drill-in.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ResellerTeamPanel from "../components/ResellerTeamPanel";
import {
  Building2,
  Users,
  Bell,
  MapPin,
  Lock,
  RefreshCw,
  DollarSign,
  Wallet,
  PiggyBank,
  X,
} from "lucide-react";

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// ---------------------------------------------------------------
// Read-only Dealer Summary Modal (opens on dealer row click)
// ---------------------------------------------------------------
const DealerSummaryModal = ({ dealer, onClose }) => {
  if (!dealer) return null;
  const economics = dealer.reseller_economics || {};
  const kpis = dealer.kpis || {};
  const hasEconomics = economics.retail_price != null || economics.cost != null;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="reseller-dealer-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="p-1.5 bg-indigo-100 rounded-md flex-shrink-0">
              <Building2 className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3
                className="text-[15px] font-semibold text-gray-900 truncate"
                data-testid="modal-dealer-name"
              >
                {dealer.name}
              </h3>
              {dealer.location?.city && (
                <p className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3 h-3" />
                  {dealer.location.city}
                  {dealer.location.state ? `, ${dealer.location.state.toUpperCase()}` : ""}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 p-1 rounded"
            data-testid="close-dealer-modal"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Billing */}
        <div className="p-5 border-b border-gray-100">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2.5">
            Billing
          </p>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-green-700 font-medium uppercase tracking-wide">
                Retail
              </p>
              <p
                className="text-[15px] font-bold text-green-800 tabular-nums mt-0.5"
                data-testid="modal-retail"
              >
                {hasEconomics ? formatCurrency(economics.retail_price) : "—"}
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-blue-700 font-medium uppercase tracking-wide">
                Cost
              </p>
              <p
                className="text-[15px] font-bold text-blue-800 tabular-nums mt-0.5"
                data-testid="modal-cost"
              >
                {hasEconomics ? formatCurrency(economics.cost) : "—"}
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5">
              <p className="text-[10px] text-emerald-700 font-medium uppercase tracking-wide">
                Margin
              </p>
              <p
                className={`text-[15px] font-bold tabular-nums mt-0.5 ${
                  (economics.monthly_margin || 0) >= 0 ? "text-emerald-800" : "text-red-600"
                }`}
                data-testid="modal-margin"
              >
                {hasEconomics ? formatCurrency(economics.monthly_margin) : "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Aggregate Stats (allowed data only) */}
        <div className="p-5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2.5">
            Performance (Last 30 days)
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className="border border-gray-200 rounded-lg px-3 py-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-purple-50 flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                  Total Leads
                </p>
                <p
                  className="text-[17px] font-bold text-gray-900 tabular-nums"
                  data-testid="modal-leads-count"
                >
                  {(kpis.leads_30d || 0).toLocaleString()}
                </p>
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg px-3 py-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-md bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Bell className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">
                  Hot Return Conversions
                </p>
                <p
                  className="text-[17px] font-bold text-gray-900 tabular-nums"
                  data-testid="modal-hot-returns"
                >
                  {(kpis.hot_returns_30d || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-100">
          <p className="text-[10px] text-gray-500 flex items-center gap-1">
            <Lock className="w-3 h-3" />
            Reseller view — aggregate only. Per-customer data is not available.
          </p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------
// Main Reseller Dashboard
// ---------------------------------------------------------------
const SubMasterDashboard = () => {
  const { user, apiFetch } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDealer, setSelectedDealer] = useState(null);

  const fetchDealers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiFetch("/submaster/my-dealers", { method: "GET" });
      setDealers(response.data?.dealers || []);
      setSummary(response.data?.summary || null);
    } catch (err) {
      console.error("Failed to fetch dealers:", err);
      setError("Failed to load dealers. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (user?.role === "SUB_MASTER") {
      fetchDealers();
    }
  }, [user, fetchDealers]);

  // Guard: Only SUB_MASTER can access
  if (user?.role !== "SUB_MASTER") {
    return (
      <div
        className="flex flex-col items-center justify-center h-64"
        data-testid="access-denied"
      >
        <Lock className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-800">Access Denied</h2>
        <p className="text-gray-600 mt-2">This page is only available for reseller accounts.</p>
      </div>
    );
  }

  return (
    <div className="p-6 w-full" data-testid="submaster-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Building2 className="w-5 h-5 text-indigo-600" />
          </div>
          <h1 className="text-[20px] font-bold text-gray-900">Reseller Dashboard</h1>
        </div>
        <p className="text-[13px] text-gray-500 ml-11">
          Your assigned dealers. Click any dealer for aggregate billing and stats.
        </p>
      </div>

      {/* Financial Summary */}
      {summary && (summary.total_retail > 0 || summary.total_cost > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
          <SummaryTile
            accent="#10B981"
            icon={DollarSign}
            label="Total Retail"
            value={formatCurrency(summary.total_retail)}
            caption="Monthly revenue"
            testId="summary-retail"
          />
          <SummaryTile
            accent="#2563EB"
            icon={Wallet}
            label="Total Cost"
            value={formatCurrency(summary.total_cost)}
            caption="Monthly expense"
            testId="summary-cost"
          />
          <SummaryTile
            accent="#059669"
            icon={PiggyBank}
            label="Monthly Margin"
            value={formatCurrency(summary.estimated_monthly_profit)}
            valueClass={
              (summary.estimated_monthly_profit || 0) >= 0 ? "text-emerald-800" : "text-red-600"
            }
            caption="Retail − Cost"
            testId="summary-margin"
          />
        </div>
      )}

      {/* KPI summary — ONLY allowed aggregates (Leads, Hot Returns) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="kpi-grid">
        <SummaryTile
          accent="#6366F1"
          icon={Building2}
          label="Assigned Dealers"
          value={dealers.length}
          caption="Your portfolio"
          testId="kpi-dealers"
        />
        <SummaryTile
          accent="#005CFF"
          icon={Users}
          label="Total Leads (30d)"
          value={dealers
            .reduce((sum, d) => sum + (d.kpis?.leads_30d || 0), 0)
            .toLocaleString()}
          caption="Aggregate across all dealers"
          testId="kpi-leads"
        />
        <SummaryTile
          accent="#F59E0B"
          icon={Bell}
          label="Hot Return Conversions (30d)"
          value={dealers
            .reduce((sum, d) => sum + (d.kpis?.hot_returns_30d || 0), 0)
            .toLocaleString()}
          caption="Returning shoppers actioned"
          testId="kpi-hot-returns"
        />
      </div>

      {/* Dealers / Team tabs */}
      <Tabs defaultValue="dealers" data-testid="reseller-dashboard-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="dealers" data-testid="tab-dealers">
            Dealers
          </TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">
            Team
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dealers">
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-[13px] font-semibold text-gray-900">Your Dealers</h2>
              <button
                onClick={fetchDealers}
                disabled={loading}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                data-testid="refresh-dealers"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw className="w-7 h-7 text-gray-400 animate-spin mx-auto mb-2" />
                <p className="text-[13px] text-gray-500">Loading dealers…</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <p className="text-red-500 mb-3 text-sm">{error}</p>
                <button
                  onClick={fetchDealers}
                  className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                >
                  Retry
                </button>
              </div>
            ) : dealers.length === 0 ? (
              <div className="p-12 text-center">
                <Building2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-[13px] text-gray-500">No dealers assigned to your account yet.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]" data-testid="reseller-dealers-table">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] font-semibold uppercase text-gray-500 tracking-wide">
                      <th className="py-2.5 px-5">Dealer</th>
                      <th className="py-2.5 px-3 text-right">Retail</th>
                      <th className="py-2.5 px-3 text-right">Cost</th>
                      <th className="py-2.5 px-3 text-right">Margin</th>
                      <th className="py-2.5 px-3 text-right">Leads (30d)</th>
                      <th className="py-2.5 px-3 text-right">Hot Returns (30d)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dealers.map((dealer) => {
                      const economics = dealer.reseller_economics || {};
                      const hasEconomics =
                        economics.retail_price != null || economics.cost != null;
                      return (
                        <tr
                          key={dealer.business_id}
                          onClick={() => setSelectedDealer(dealer)}
                          className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                          data-testid={`dealer-row-${dealer.business_id}`}
                        >
                          <td className="py-3 px-5">
                            <div className="flex items-center gap-2.5">
                              <div
                                className={`p-1.5 rounded-md flex-shrink-0 ${
                                  dealer.is_active ? "bg-green-50" : "bg-gray-100"
                                }`}
                              >
                                <Building2
                                  className={`w-4 h-4 ${
                                    dealer.is_active ? "text-green-600" : "text-gray-400"
                                  }`}
                                />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="font-semibold text-gray-900 truncate">
                                    {dealer.name}
                                  </span>
                                  {!dealer.is_active && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                {dealer.location?.city && (
                                  <p className="text-[11px] text-gray-500 truncate">
                                    {dealer.location.city}
                                    {dealer.location.state
                                      ? `, ${dealer.location.state.toUpperCase()}`
                                      : ""}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3 text-right tabular-nums font-medium text-gray-900">
                            {hasEconomics ? formatCurrency(economics.retail_price) : "—"}
                          </td>
                          <td className="py-3 px-3 text-right tabular-nums text-gray-700">
                            {hasEconomics ? formatCurrency(economics.cost) : "—"}
                          </td>
                          <td
                            className={`py-3 px-3 text-right tabular-nums font-semibold ${
                              (economics.monthly_margin || 0) >= 0
                                ? "text-emerald-700"
                                : "text-red-600"
                            }`}
                          >
                            {hasEconomics ? formatCurrency(economics.monthly_margin) : "—"}
                          </td>
                          <td className="py-3 px-3 text-right tabular-nums font-semibold text-gray-900">
                            {(dealer.kpis?.leads_30d || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-right tabular-nums font-semibold text-gray-900">
                            {(dealer.kpis?.hot_returns_30d || 0).toLocaleString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer note */}
          <div className="mt-4 text-center text-[12px] text-gray-500">
            <p className="inline-flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              Reseller view. Per-customer data (names, phones, vehicles) is not visible at this
              access level.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="team">
          <ResellerTeamPanel canManage={user?.reseller_permission === "ADMIN"} />
        </TabsContent>
      </Tabs>

      {/* Read-only per-dealer detail modal */}
      {selectedDealer && (
        <DealerSummaryModal dealer={selectedDealer} onClose={() => setSelectedDealer(null)} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------
// Compact left-border summary tile (enterprise style)
// ---------------------------------------------------------------
const SummaryTile = ({ accent, icon: Icon, label, value, caption, valueClass, testId }) => (
  <div
    className="bg-white rounded-lg border border-gray-200 px-4 py-3 flex items-center gap-3"
    style={{ borderLeft: `4px solid ${accent}`, minHeight: 86 }}
    data-testid={testId}
  >
    <div
      className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}15` }}
    >
      <Icon size={18} style={{ color: accent }} />
    </div>
    <div className="min-w-0">
      <p className={`text-xl font-bold leading-tight tabular-nums ${valueClass || "text-gray-900"}`}>
        {value}
      </p>
      <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mt-0.5">
        {label}
      </p>
      {caption && <p className="text-[10px] text-gray-400 mt-0.5">{caption}</p>}
    </div>
  </div>
);

export default SubMasterDashboard;
